import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import io from 'socket.io-client';
import api from '../utils/api';
import MetaTags from '../components/MetaTags';
import { getSocketUrl, getAudienceUrl } from '../utils/urlHelper';
import QRCode from 'qrcode';
import { normalizeQrTemplate, hasQrCallToAction, qrBackgroundColor, getActiveQrSettings, positiveQrSize } from '../utils/qrTemplateSettings';

const DisplayView = ({ sessionId, initialTemplate = null }) => {
  const { sessionId: routeSessionId } = useParams();
  const finalSessionId = sessionId || routeSessionId;
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentPoll, setCurrentPoll] = useState(null);
  const [pollDisplayMode, setPollDisplayMode] = useState('display'); // 'display' or 'results'
  const [socket, setSocket] = useState(null);
  const [animationState, setAnimationState] = useState('none');
  const [sessionName, setSessionName] = useState('');
  const [error, setError] = useState(null);
  const [template, setTemplate] = useState(initialTemplate);
  const [pendingDisplayModeUpdate, setPendingDisplayModeUpdate] = useState(null);
  const [pollSettings, setPollSettings] = useState({
    language: 'en',
    gradient_start: '#667eea',
    gradient_end: '#764ba2'
  });
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [qrAnimationState, setQrAnimationState] = useState('none');
  const [qrTemplate, setQrTemplate] = useState({
    call_to_action: 'Ask a question',
    background_color: '#1e3a8a',
    text_color: '#ffffff',
    qr_size: 300,
    custom_css: ''
  });
  const [particles, setParticles] = useState([]);
  const [nameTags, setNameTags] = useState([]);
  const [nameTagAnimations, setNameTagAnimations] = useState({});
  const [nameTagSettings, setNameTagSettings] = useState({
    background_color: '#ffffff',
    name_color: '#000000',
    subtitle_color: '#666666',
    name_font_size: '24px',
    subtitle_font_size: '16px',
    name_font_weight: 'bold',
    subtitle_font_weight: 'normal',
    padding: '12px 20px',
    borderRadius: '8px',
    minWidth: '300px',
    maxWidth: '300px',
    subtitle_margin_top: '0px'
  });
  const previousNameTagsRef = useRef([]);
  const timeoutRef = useRef(null);
  const socketRef = useRef(null);
  const animatingPollRef = useRef(null); // Track which poll is currently animating

  // Fetch poll settings
  useEffect(() => {
    const fetchPollSettings = async () => {
      try {
        const response = await api.get(`/api/poll-settings/session/${finalSessionId}`);
        setPollSettings({
          language: response.data.language || 'en',
          gradient_start: response.data.gradient_start || '#667eea',
          gradient_end: response.data.gradient_end || '#764ba2'
        });
      } catch (err) {
        console.error('Error fetching poll settings:', err);
        // Use defaults if fetch fails
      }
    };
    
    fetchPollSettings();
  }, [finalSessionId]);

  // Fetch name tag settings
  useEffect(() => {
    const fetchNameTagSettings = async () => {
      try {
        const response = await api.get(`/api/sessions/${finalSessionId}/name-tag-settings`);
        if (response.data.settings) {
          setNameTagSettings(response.data.settings);
        }
      } catch (err) {
        console.error('Error fetching name tag settings:', err);
      }
    };
    
    fetchNameTagSettings();
  }, [finalSessionId]);

  // Fetch name tags
  useEffect(() => {
    const fetchNameTags = async () => {
      try {
        const response = await api.get(`/api/name-tags/session/${finalSessionId}`);
        const newNameTags = response.data;
        
        // Handle animations for name tags going on/off air
        const previousOnAirIds = new Set(previousNameTagsRef.current.filter(tag => tag.is_on_air).map(tag => tag.id));
        const newOnAirIds = new Set(newNameTags.filter(tag => tag.is_on_air).map(tag => tag.id));
        
        // Find name tags that just went on air
        const newlyOnAir = new Set([...newOnAirIds].filter(id => !previousOnAirIds.has(id)));
        // Find name tags that just went off air
        const newlyOffAir = new Set([...previousOnAirIds].filter(id => !newOnAirIds.has(id)));
        
        // Set fade-in animation for newly on-air name tags
        if (newlyOnAir.size > 0) {
          setNameTagAnimations(prev => {
            const newAnimations = { ...prev };
            newlyOnAir.forEach(id => {
              newAnimations[id] = 'fade-in';
            });
            return newAnimations;
          });
          
          // Clear animation after it completes
          setTimeout(() => {
            setNameTagAnimations(prev => {
              const updated = { ...prev };
              newlyOnAir.forEach(id => {
                delete updated[id];
              });
              return updated;
            });
          }, 500);
        }
        
        // Set fade-out animation for newly off-air name tags
        if (newlyOffAir.size > 0) {
          setNameTagAnimations(prev => {
            const newAnimations = { ...prev };
            newlyOffAir.forEach(id => {
              newAnimations[id] = 'fade-out';
            });
            return newAnimations;
          });
          
          // Remove from state after animation completes
          setTimeout(() => {
            setNameTagAnimations(prev => {
              const updated = { ...prev };
              newlyOffAir.forEach(id => {
                delete updated[id];
              });
              return updated;
            });
            
            // Remove the name tag from the main state after fade-out
            setNameTags(prev => prev.filter(tag => !newlyOffAir.has(tag.id)));
            previousNameTagsRef.current = previousNameTagsRef.current.filter(tag => !newlyOffAir.has(tag.id));
          }, 500);
        }
        
        // Update the ref with the new name tags
        previousNameTagsRef.current = newNameTags;
        setNameTags(newNameTags);
      } catch (error) {
        console.error('Error fetching name tags:', error);
      }
    };

    fetchNameTags();
  }, [finalSessionId]);

  // Fetch QR template settings
  useEffect(() => {
    const fetchQRTemplate = async () => {
      try {
        const response = await api.get(`/api/qr-templates/session/${finalSessionId}`);
        setQrTemplate(normalizeQrTemplate(response.data));
      } catch (err) {
        console.error('Error fetching QR template:', err);
        // Use defaults if fetch fails
      }
    };
    
    fetchQRTemplate();
  }, [finalSessionId]);

  // Fetch session QR code visibility state
  useEffect(() => {
    const fetchSessionQRState = async () => {
      try {
        const response = await api.get(`/api/sessions/${finalSessionId}`);
        const session = response.data.session;
        if (session && session.qr_code_visible) {
          console.log('QR code was visible before refresh, restoring state');
          setQrAnimationState('fadeIn');
          setShowQRCode(true);
        }
      } catch (err) {
        console.error('Error fetching session QR state:', err);
      }
    };
    
    fetchSessionQRState();
  }, [finalSessionId]);

  // Generate QR code when needed
  useEffect(() => {
    const generateQRCode = async () => {
      if (!showQRCode) return;
      
      try {
        const audienceUrl = getAudienceUrl(finalSessionId);
        const activeQrSettings = getActiveQrSettings(qrTemplate);
        const qrDataUrl = await QRCode.toDataURL(audienceUrl, {
          width: positiveQrSize(activeQrSettings.qr_size),
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrCodeDataUrl(qrDataUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };
    
    generateQRCode();
  }, [showQRCode, finalSessionId, qrTemplate.display_mode, qrTemplate.qr_size, qrTemplate.modes]);

  // Connect to socket and fetch initial data
  useEffect(() => {
    // Get socket URL from helper (automatically handles dev/prod)
    const socketUrl = getSocketUrl();
    
    console.log(`Connecting to socket at ${socketUrl}`);
    console.log('NODE_ENV:', process.env.NODE_ENV);
    
    const newSocket = io(socketUrl, {
      path: '/socket.io/',
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      transports: ['websocket', 'polling'],
      withCredentials: true
    });
    
    // Store socket in ref for cleanup
    socketRef.current = newSocket;
    
    // Add connection event handlers
    newSocket.on('connect', () => {
      console.log('=== DISPLAYVIEW SOCKET CONNECTED ===');
      console.log('Socket connected:', newSocket.id);
      console.log('Session ID:', finalSessionId);
      
      // Join the session room
      const roomName = `session-${finalSessionId}`;
      console.log(`DisplayView joining room: ${roomName}`);
      newSocket.emit('join-room', roomName);
      console.log('=== END DISPLAYVIEW SOCKET CONNECTED ===');
    });
    
    newSocket.on('disconnect', (reason) => {
      console.log(`Socket disconnected: ${reason}`);
      if (reason === 'io server disconnect') {
        // The disconnection was initiated by the server, try to reconnect
        newSocket.connect();
      }
    });
    
    newSocket.on('error', (error) => {
      console.error('Socket error:', error);
    });
    
    newSocket.on('reconnect', (attemptNumber) => {
      console.log(`Socket reconnected after ${attemptNumber} attempts`);
      const roomName = `session-${finalSessionId}`;
      console.log(`Rejoining room after reconnection: ${roomName}`);
      newSocket.emit('join-room', roomName);
    });
    
    setSocket(newSocket);
    
    // Fetch session info and template
    const fetchData = async () => {
      try {
        // Fetch session info
        const sessionResponse = await api.get(`/api/sessions/${finalSessionId}`);
        setSessionName(sessionResponse.data.name || 'Live Q&A Session');
        
        // Fetch template if not provided
        if (!initialTemplate) {
          try {
            const templateResponse = await api.get(`/api/sessions/${finalSessionId}/template`);
            if (templateResponse.data) {
              console.log('Fetched template:', templateResponse.data);
              setTemplate(templateResponse.data);
            }
          } catch (err) {
            console.error('Error fetching template:', err);
          }
        }
        
        
        // Fetch current on-air question
        const questionsResponse = await api.get(`/api/sessions/${finalSessionId}/questions`);
        const onAirQuestion = questionsResponse.data.find(q => q.status === 'on_air');
        if (onAirQuestion) {
          setCurrentQuestion(onAirQuestion);
        }
        
        // Fetch current display state (on-air poll with display mode)
        try {
          const displayStateResponse = await api.get(`/api/polls/session/${finalSessionId}/display-state`);
          const { hasOnAirPoll, poll, displayMode } = displayStateResponse.data;
          
          if (hasOnAirPoll && poll) {
            console.log('Found on-air poll on load:', poll);
            console.log('Display mode:', displayMode);
            
            const pollWithDisplayMode = {
              ...poll,
              displayMode: displayMode
            };
            
            // If display mode is results, fetch the results
            if (displayMode === 'results') {
              try {
                const resultsResponse = await api.get(`/api/polls/${poll.id}/with-results`);
                pollWithDisplayMode.results = resultsResponse.data.results;
                console.log('Fetched results for on-air poll:', resultsResponse.data.results);
              } catch (err) {
                console.error('Error fetching results for on-air poll:', err);
              }
            }
            
            // Only update if the poll is different from the current one
            setCurrentPoll(prev => {
              if (!prev || prev.id !== poll.id || prev.displayMode !== displayMode) {
                console.log('Updating currentPoll from fetchData');
                return pollWithDisplayMode;
              }
              console.log('Keeping existing currentPoll, no change needed');
              return prev;
            });
            setCurrentQuestion(null); // Clear any current question
          } else {
            // Only clear currentPoll if there's no on-air poll and we currently have one
            setCurrentPoll(prev => {
              if (prev) {
                console.log('Clearing currentPoll - no on-air poll found');
                return null;
              }
              return prev;
            });
          }
        } catch (err) {
          console.error('Error fetching display state:', err);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load display data');
      }
    };
    
    fetchData();
    
    // Cleanup function
    return () => {
      console.log('Cleaning up socket connection');
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [finalSessionId, initialTemplate]);

  // Handle page lifecycle changes for mobile browser reconnection
  useEffect(() => {
    const reconnectSocket = () => {
      console.log('Page became active, reconnecting socket...');
      const socketUrl = getSocketUrl();
      const newSocket = io(socketUrl, {
        path: '/socket.io/',
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 20000,
        transports: ['websocket', 'polling'],
        withCredentials: true
      });
      
      // Store socket in ref for cleanup
      socketRef.current = newSocket;
      
      // Add connection event handlers
      newSocket.on('connect', () => {
        console.log('Socket reconnected after lifecycle change:', newSocket.id);
        const roomName = `session-${finalSessionId}`;
        console.log(`Re-joining room after lifecycle change: ${roomName}`);
        newSocket.emit('join-room', roomName);
      });
      
      newSocket.on('disconnect', (reason) => {
        console.log(`Socket disconnected: ${reason}`);
        if (reason === 'io server disconnect') {
          newSocket.connect();
        }
      });
      
      newSocket.on('error', (error) => {
        console.error('Socket error:', error);
      });
      
      newSocket.on('reconnect', (attemptNumber) => {
        console.log(`Socket reconnected after ${attemptNumber} attempts`);
        const roomName = `session-${finalSessionId}`;
        console.log(`Rejoining room after reconnection: ${roomName}`);
        newSocket.emit('join-room', roomName);
      });
      
      setSocket(newSocket);
    };

    const rejoinRoom = () => {
      if (socket && socket.connected) {
        console.log('Page became active, re-joining session room...');
        const roomName = `session-${finalSessionId}`;
        socket.emit('join-room', roomName);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && socket) {
        if (socket.disconnected) {
          reconnectSocket();
        } else {
          rejoinRoom();
        }
      }
    };

    // Handle Page Lifecycle API states
    const handlePageLifecycleChange = (event) => {
      console.log('Page lifecycle state changed:', event.newState);
      
      switch (event.newState) {
        case 'active':
          // Page became active - reconnect if needed
          if (socket && socket.disconnected) {
            reconnectSocket();
          } else if (socket && socket.connected) {
            rejoinRoom();
          }
          break;
        case 'passive':
        case 'hidden':
          // Page is becoming inactive - no action needed
          console.log('Page becoming inactive:', event.newState);
          break;
        case 'frozen':
          // Page is frozen - socket will be disconnected
          console.log('Page frozen - socket will be disconnected');
          break;
        case 'terminated':
          // Page is terminated - socket will be disconnected
          console.log('Page terminated - socket will be disconnected');
          break;
      }
    };

    // Handle page resume from frozen state
    const handlePageResume = () => {
      console.log('Page resumed from frozen state');
      if (socket && socket.disconnected) {
        reconnectSocket();
      } else if (socket && socket.connected) {
        rejoinRoom();
      }
    };

    // Handle page show (back/forward cache)
    const handlePageShow = (event) => {
      console.log('Page show event, persisted:', event.persisted);
      if (event.persisted) {
        // Page was restored from back/forward cache
        if (socket && socket.disconnected) {
          reconnectSocket();
        } else if (socket && socket.connected) {
          rejoinRoom();
        }
      }
    };

    // Add all event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Page Lifecycle API events
    if ('onpagehide' in window) {
      document.addEventListener('pagehide', handlePageLifecycleChange);
    }
    if ('onpageshow' in window) {
      document.addEventListener('pageshow', handlePageShow);
    }
    if ('onfreeze' in document) {
      document.addEventListener('freeze', handlePageLifecycleChange);
    }
    if ('onresume' in document) {
      document.addEventListener('resume', handlePageResume);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if ('onpagehide' in window) {
        document.removeEventListener('pagehide', handlePageLifecycleChange);
      }
      if ('onpageshow' in window) {
        document.removeEventListener('pageshow', handlePageShow);
      }
      if ('onfreeze' in document) {
        document.removeEventListener('freeze', handlePageLifecycleChange);
      }
      if ('onresume' in document) {
        document.removeEventListener('resume', handlePageResume);
      }
    };
  }, [socket, finalSessionId]);

  // Set up socket event listeners
  useEffect(() => {
    if (!socket) return;

    console.log('=== DISPLAYVIEW: SETTING UP SOCKET EVENT LISTENERS ===');
    console.log('Socket ID:', socket.id);
    console.log('Socket connected:', socket.connected);
    console.log('Session ID:', finalSessionId);
    
    // Add a general socket event listener to debug what events are being received
    socket.onAny((eventName, ...args) => {
      console.log('=== DISPLAYVIEW: RECEIVED SOCKET EVENT ===');
      console.log('Event name:', eventName);
      console.log('Args:', args);
      console.log('=== END DISPLAYVIEW: RECEIVED SOCKET EVENT ===');
    });

    const handleStatusChanged = (data) => {
      console.log('Handling status change:', data);
      
      // Handle both event formats (new format with newStatus/oldStatus and question, or direct question object)
      if (data.newStatus === 'on_air' || data.status === 'on_air') {
        // A question was set to on_air
        const question = data.question || data;
        console.log('Question set to on-air:', question);
        
        // Fade out current question if there is one
        setAnimationState('fadeOut');
        
        // Clear any existing timeouts
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        // After animation completes, update the question and fade in
        timeoutRef.current = setTimeout(() => {
          setCurrentQuestion(question);
          setAnimationState('fadeIn');
          
          // Reset animation state after fade in completes
          timeoutRef.current = setTimeout(() => {
            setAnimationState('none');
          }, 500);
        }, 500);
      } else {
        // Question was taken off air - use functional form to get current state
        setCurrentQuestion(current => {
          if (current) {
            // Check if the current question was taken off air
            const questionId = data.question ? data.question.id : data.id;
            
            if (current.id === questionId) {
              console.log('Current question taken off air, fading out');
              setAnimationState('fadeOut');
              
              // Clear any existing timeouts
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              
              // After animation completes, remove the question
              timeoutRef.current = setTimeout(() => {
                setCurrentQuestion(null);
                setAnimationState('none');
              }, 500);
            }
          }
          
          // Return current state unchanged
          return current;
        });
      }
    };
    
    // Listen for question status updates
    socket.on('question-status-updated', (data) => {
      console.log('Received question-status-updated event:', data);
      
      // Fetch the question details if we only have the ID
      if (data.questionId && !data.question) {
        api.get(`/api/questions/${data.questionId}`)
          .then(response => {
            handleStatusChanged({
              ...data,
              question: response.data,
              newStatus: response.data.status
            });
          })
          .catch(err => {
            console.error('Error fetching question details:', err);
          });
      } else {
        handleStatusChanged(data);
      }
    });
    
    // Listen for direct question updates
    socket.on('question-updated', (updatedQuestion) => {
      console.log('Received question-updated event:', updatedQuestion);
      
      // If this is the current question, update it
      if (currentQuestion && currentQuestion.id === updatedQuestion.id) {
        setCurrentQuestion(updatedQuestion);
      }
    });
    
    // Listen for question deletions
    socket.on('question-deleted', (questionId) => {
      console.log('Received question-deleted event:', questionId);
      
      // If this is the current question, remove it
      if (currentQuestion && currentQuestion.id === questionId) {
        setAnimationState('fadeOut');
        
        // Clear any existing timeouts
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        // After animation completes, remove the question
        timeoutRef.current = setTimeout(() => {
          setCurrentQuestion(null);
          setAnimationState('none');
        }, 500);
      }
    });
    
    // Listen for template updates
    socket.on('template-updated', (updatedTemplate) => {
      console.log('Received template-updated event:', updatedTemplate);
      setTemplate(updatedTemplate);
    });
    
    // Listen for poll settings updates
    socket.on('poll-settings-updated', (data) => {
      console.log('Received poll-settings-updated event:', data);
      if (String(data.sessionId) === String(finalSessionId)) {
        console.log('Poll settings updated for this session');
        setPollSettings({
          language: data.language || 'en',
          gradient_start: data.gradient_start || '#667eea',
          gradient_end: data.gradient_end || '#764ba2'
        });
      }
    });
    
    // Listen for template assignments
    socket.on('template-assigned', (data) => {
      console.log('Received template-assigned event:', data);
      console.log('Current session ID:', finalSessionId);
      
      // Compare as strings to avoid type mismatches
      if (String(data.sessionId) === String(finalSessionId)) {
        console.log('Template assignment is for this session, fetching template');
        // Fetch the updated template
        api.get(`/api/templates/${data.templateId}`)
          .then(response => {
            console.log('Fetched assigned template:', response.data);
            setTemplate(response.data);
          })
          .catch(err => {
            console.error('Error fetching assigned template:', err);
          });
      } else {
        console.log('Template assignment is not for this session');
      }
    });
    
    // Listen for poll status updates (when poll is set "on air")
    socket.on('poll-status-updated', (data) => {
      console.log('=== POLL STATUS UPDATED EVENT ===');
      console.log('Received poll-status-updated event:', data);
      console.log('Current session ID:', finalSessionId);
      console.log('Current poll:', currentPoll);
      console.log('isOnAir value:', data.isOnAir);
      console.log('Session ID match:', String(data.sessionId) === String(finalSessionId));
      
      // Check if this poll is for our session and is being set on air
      if (String(data.sessionId) === String(finalSessionId) && data.isOnAir) {
        console.log('Poll is being set on air for this session, fetching poll details');
        console.log('Current poll state:', currentPoll ? `Poll ID ${currentPoll.id}` : 'No poll');
        console.log('Incoming poll ID:', data.pollId);
        console.log('Animation state:', animationState);
        
        // Check if this poll is already displayed - if so, skip animation
        if (currentPoll && String(currentPoll.id) === String(data.pollId)) {
          console.log('Poll is already displayed, skipping animation');
          return;
        }
        
        // If we're already animating, cancel and restart
        if (timeoutRef.current) {
          console.log('Clearing existing animation timeout');
          clearTimeout(timeoutRef.current);
        }
        
        // Fetch the poll details from the database
        api.get(`/api/polls/${data.pollId}`)
          .then(response => {
            const poll = response.data;
            console.log('Fetched poll for display:', poll);
            console.log('Poll options from API:', poll.options);
            console.log('Poll options type from API:', typeof poll.options);
            
            // Use display mode from database
            const pollWithDisplayMode = {
              ...poll,
              displayMode: poll.display_mode || 'question'
            };
            
            // If display mode is results, fetch the results
            if (pollWithDisplayMode.displayMode === 'results') {
              api.get(`/api/polls/${data.pollId}/with-results`)
                .then(resultsResponse => {
                  pollWithDisplayMode.results = resultsResponse.data.results;
                  console.log('Fetched results for on-air poll:', resultsResponse.data.results);
                  
                  // Only fade out if there's current content
                  if (currentQuestion || currentPoll) {
                    setAnimationState('fadeOut');
                    
                    // Clear any existing timeouts
                    if (timeoutRef.current) {
                      clearTimeout(timeoutRef.current);
                    }
                    
                    // After animation completes, show the poll and fade in
                    timeoutRef.current = setTimeout(() => {
                      setCurrentPoll(pollWithDisplayMode);
                      setCurrentQuestion(null);
                      setAnimationState('fadeIn');
                      
                      // Reset animation state after fade in completes
                      timeoutRef.current = setTimeout(() => {
                        setAnimationState('none');
                      }, 500);
                    }, 500);
                  } else {
                    // No current content, just fade in
                    setCurrentPoll(pollWithDisplayMode);
                    setCurrentQuestion(null);
                    setAnimationState('fadeIn');
                    
                    timeoutRef.current = setTimeout(() => {
                      setAnimationState('none');
                    }, 500);
                  }
                })
                .catch(err => {
                  console.error('Error fetching results for on-air poll:', err);
                  
                  // Still show the poll even if results fetch fails
                  if (currentQuestion || currentPoll) {
                    setAnimationState('fadeOut');
                    if (timeoutRef.current) {
                      clearTimeout(timeoutRef.current);
                    }
                    timeoutRef.current = setTimeout(() => {
                      setCurrentPoll(pollWithDisplayMode);
                      setCurrentQuestion(null);
                      setAnimationState('fadeIn');
                      timeoutRef.current = setTimeout(() => {
                        setAnimationState('none');
                      }, 500);
                    }, 500);
                  } else {
                    setCurrentPoll(pollWithDisplayMode);
                    setCurrentQuestion(null);
                    setAnimationState('fadeIn');
                    timeoutRef.current = setTimeout(() => {
                      setAnimationState('none');
                    }, 500);
                  }
                });
            } else {
              // Question mode - simpler flow
              // Clear any existing timeouts
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              
              // Just show the poll with fade in (no fade out needed)
              setCurrentPoll(pollWithDisplayMode);
              setCurrentQuestion(null);
              setAnimationState('fadeIn');
              
              timeoutRef.current = setTimeout(() => {
                setAnimationState('none');
              }, 500);
            }
          })
          .catch(err => {
            console.error('Error fetching poll details:', err);
          });
      } else if (String(data.sessionId) === String(finalSessionId) && !data.isOnAir) {
        // Poll is being taken off air
        console.log('Poll is being taken off air');
        console.log('Current poll ID:', currentPoll?.id, 'Event poll ID:', data.pollId);
        
        // If we have a current poll and it matches the event poll ID, or if we don't have a current poll
        // (which means we should clear any poll that might be displayed)
        if (!currentPoll || (currentPoll && String(currentPoll.id) === String(data.pollId))) {
          console.log('Removing poll from display');
          setAnimationState('fadeOut');
          
          // Clear any existing timeouts
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          
          // After animation completes, remove the poll
          timeoutRef.current = setTimeout(() => {
            setCurrentPoll(null);
            setAnimationState('none');
          }, 500);
        } else {
          console.log('Current poll ID does not match event poll ID, not removing poll');
        }
      } else {
        console.log('❌ Poll status update is not for this session or not setting on air');
        console.log('Event session ID:', data.sessionId, 'Current session ID:', finalSessionId);
        console.log('Event isOnAir:', data.isOnAir);
        console.log('Session match:', String(data.sessionId) === String(finalSessionId));
        console.log('Is on air:', data.isOnAir);
      }
      console.log('=== END POLL STATUS UPDATED EVENT ===');
    });

        // Listen for poll display mode updates
        socket.on('poll-display-mode-updated', (data) => {
          console.log('=== POLL DISPLAY MODE UPDATED EVENT ===');
          console.log('Received poll-display-mode-updated event:', data);
          console.log('Current poll:', currentPoll);
          console.log('Current poll ID:', currentPoll?.id, 'Type:', typeof currentPoll?.id);
          console.log('Event poll ID:', data.pollId, 'Type:', typeof data.pollId);
          console.log('String comparison:', String(currentPoll?.id) === String(data.pollId));
          
          // Store the pending display mode update
          setPendingDisplayModeUpdate(data);
          
          console.log('=== END POLL DISPLAY MODE UPDATED EVENT ===');
        });

    // Listen for name tag updates (on-air status AND position changes)
    socket.on('name-tag-on-air-updated', (data) => {
      console.log('=== DISPLAYVIEW: NAME TAG ON AIR UPDATED EVENT ===');
      console.log('DisplayView received name-tag-on-air-updated event:', data);
      console.log('DisplayView current session ID:', finalSessionId);
      console.log('DisplayView socket ID:', socket.id);
      console.log('DisplayView socket connected:', socket.connected);
      
      if (String(data.sessionId) === String(finalSessionId)) {
        console.log('✅ DisplayView: Name tag updated for this session (on-air or position change)');
        console.log('Fetching updated name tags from API...');
        
        // Fetch updated name tags from API (like polls do)
        api.get(`/api/name-tags/session/${finalSessionId}`)
          .then(response => {
            const updatedNameTags = response.data;
            console.log('DisplayView: Fetched updated name tags:', updatedNameTags);
            
            // Handle animations for name tags going on/off air
            const previousOnAirIds = new Set(previousNameTagsRef.current.filter(tag => tag.is_on_air).map(tag => tag.id));
            const newOnAirIds = new Set(updatedNameTags.filter(tag => tag.is_on_air).map(tag => tag.id));
            
            // Find name tags that just went on air
            const newlyOnAir = new Set([...newOnAirIds].filter(id => !previousOnAirIds.has(id)));
            // Find name tags that just went off air
            const newlyOffAir = new Set([...previousOnAirIds].filter(id => !newOnAirIds.has(id)));
            
            // Set fade-in animation for newly on-air name tags
            if (newlyOnAir.size > 0) {
              setNameTagAnimations(prev => {
                const newAnimations = { ...prev };
                newlyOnAir.forEach(id => {
                  newAnimations[id] = 'fade-in';
                });
                return newAnimations;
              });
              
              // Clear animation after it completes
              setTimeout(() => {
                setNameTagAnimations(prev => {
                  const updated = { ...prev };
                  newlyOnAir.forEach(id => {
                    delete updated[id];
                  });
                  return updated;
                });
              }, 500);
            }
            
            // Set fade-out animation for newly off-air name tags
            if (newlyOffAir.size > 0) {
              setNameTagAnimations(prev => {
                const newAnimations = { ...prev };
                newlyOffAir.forEach(id => {
                  newAnimations[id] = 'fade-out';
                });
                return newAnimations;
              });
              
              // Remove from state after animation completes
              setTimeout(() => {
                setNameTagAnimations(prev => {
                  const updated = { ...prev };
                  newlyOffAir.forEach(id => {
                    delete updated[id];
                  });
                  return updated;
                });
                
                // Remove the name tag from the main state after fade-out
                setNameTags(prev => prev.filter(tag => !newlyOffAir.has(tag.id)));
                previousNameTagsRef.current = previousNameTagsRef.current.filter(tag => !newlyOffAir.has(tag.id));
              }, 500);
            }
            
            // Update the ref with the new name tags
            previousNameTagsRef.current = updatedNameTags;
            setNameTags(updatedNameTags);
          })
          .catch(err => {
            console.error('DisplayView: Error fetching updated name tags:', err);
          });
      } else {
        console.log('❌ DisplayView: Name tag update is not for this session');
        console.log('Expected session:', finalSessionId, 'Got session:', data.sessionId);
      }
      console.log('=== END DISPLAYVIEW: NAME TAG ON AIR UPDATED EVENT ===');
    });

    // Listen for name tag updates (when name tag is edited)
    socket.on('name-tag-updated', (data) => {
      console.log('=== DISPLAYVIEW: NAME TAG UPDATED EVENT ===');
      console.log('DisplayView received name-tag-updated event:', data);
      console.log('DisplayView current session ID:', finalSessionId);
      console.log('DisplayView socket ID:', socket.id);
      console.log('DisplayView socket connected:', socket.connected);
      
      if (String(data.sessionId) === String(finalSessionId)) {
        console.log('✅ DisplayView: Name tag updated for this session');
        console.log('Fetching updated name tags from API...');
        
        // Fetch updated name tags from API (like polls do)
        api.get(`/api/name-tags/session/${finalSessionId}`)
          .then(response => {
            const updatedNameTags = response.data;
            console.log('DisplayView: Fetched updated name tags after edit:', updatedNameTags);
            setNameTags(updatedNameTags);
          })
          .catch(err => {
            console.error('DisplayView: Error fetching updated name tags after edit:', err);
          });
      } else {
        console.log('❌ DisplayView: Name tag update is not for this session');
        console.log('Expected session:', finalSessionId, 'Got session:', data.sessionId);
      }
      console.log('=== END DISPLAYVIEW: NAME TAG UPDATED EVENT ===');
    });

    // Listen for name tag settings updates
    socket.on('name-tag-settings-updated', (data) => {
      console.log('=== DISPLAYVIEW: NAME TAG SETTINGS UPDATED EVENT ===');
      if (String(data.sessionId) === String(finalSessionId)) {
        setNameTagSettings(data.settings);
      }
    });

    // Listen for poll vote updates (when someone votes)
    socket.on('poll-vote-updated', (data) => {
      console.log('=== POLL VOTE UPDATED EVENT ===');
      console.log('Received poll-vote-updated event:', data);
      
      // Use functional form to get the current state
      setCurrentPoll(prev => {
        console.log('Current poll in vote update:', prev);
        console.log('Current poll ID:', prev?.id, 'Type:', typeof prev?.id);
        console.log('Event poll ID:', data.pollId, 'Type:', typeof data.pollId);
        console.log('Current display mode:', prev?.displayMode);
        console.log('String comparison:', String(prev?.id) === String(data.pollId));
        
        if (prev && String(prev.id) === String(data.pollId)) {
          console.log('✅ Poll ID matches! Poll vote updated, refreshing results');
          
          // If we're currently showing results, fetch the updated results
          if (prev.displayMode === 'results') {
            console.log('✅ Poll is in results mode, fetching updated results');
            api.get(`/api/polls/${data.pollId}/with-results`)
              .then(response => {
                const pollWithResults = response.data;
                console.log('✅ Fetched updated poll results:', pollWithResults);
                
                setCurrentPoll(current => ({
                  ...current,
                  results: pollWithResults.results
                }));
                console.log('✅ Updated currentPoll with new results');
              })
              .catch(err => {
                console.error('❌ Error fetching updated poll results:', err);
              });
          } else {
            console.log('❌ Poll is not in results mode, not updating results');
          }
        } else {
          console.log('❌ Poll ID does not match or no current poll');
          console.log('Current poll exists:', !!prev);
          if (prev) {
            console.log('ID comparison failed:', {
              current: prev.id,
              event: data.pollId,
              currentType: typeof prev.id,
              eventType: typeof data.pollId
            });
          }
        }
        console.log('=== END POLL VOTE UPDATED EVENT ===');
        
        // Return the current state unchanged
        return prev;
      });
    });
    
    
    // Listen for QR code display control
    socket.on('show-qr-code', (data) => {
      console.log('Received show-qr-code event:', data);
      if (String(data.sessionId) === String(finalSessionId)) {
        setCurrentQuestion(null);
        setCurrentPoll(null);
        setQrAnimationState('fadeIn');
        setShowQRCode(true);
      }
    });

    socket.on('hide-qr-code', (data) => {
      console.log('Received hide-qr-code event:', data);
      if (String(data.sessionId) === String(finalSessionId)) {
        setQrAnimationState('fadeOut');
        
        // Clear any existing timeouts
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        // Set timeout to hide QR code after fade out animation completes
        timeoutRef.current = setTimeout(() => {
          setShowQRCode(false);
          setQrAnimationState('none');
        }, 500); // Match the CSS animation duration
      }
    });

    socket.on('qr-template-updated', (data) => {
      console.log('Received qr-template-updated event:', data);
      console.log('Current session ID:', finalSessionId);
      console.log('Event session ID:', data.sessionId);
      if (String(data.sessionId) === String(finalSessionId)) {
        console.log('Updating QR template with new data:', data.template);
        setQrTemplate(normalizeQrTemplate(data.template));
      } else {
        console.log('Session ID mismatch, ignoring template update');
      }
    });

    // Listen for audience reactions (for particle effects on display)
    socket.on('audience-reaction', (data) => {
      console.log('Received audience-reaction event in DisplayView:', data);
      if (String(data.sessionId) === String(finalSessionId)) {
        // Create particle effect from bottom of screen with random horizontal position
        const position = {
          x: Math.random() * window.innerWidth, // Always random horizontal position
          y: window.innerHeight - 50 // Spawn from bottom
        };
        createParticle(data.reaction, position);
      }
    });

    // Listen for session name updates
    socket.on('session-name-updated', (data) => {
      console.log('Received session-name-updated event in DisplayView:', data);
      if (String(data.sessionId) === String(finalSessionId)) {
        setSessionName(data.name);
      }
    });

    // Clean up event listeners when component unmounts or socket changes
    return () => {
      console.log('Cleaning up socket event listeners in DisplayView');
      socket.off('question-status-updated');
      socket.off('question-updated');
      socket.off('question-deleted');
      socket.off('template-updated');
      socket.off('template-assigned');
      socket.off('poll-status-updated');
      socket.off('poll-display-mode-updated');
      socket.off('poll-vote-updated');
      socket.off('name-tag-on-air-updated');
      socket.off('name-tag-updated');
      socket.off('name-tag-settings-updated');
      socket.off('show-qr-code');
      socket.off('hide-qr-code');
      socket.off('qr-template-updated');
      socket.off('audience-reaction');
      socket.off('session-name-updated');
      socket.offAny(); // Clean up the general event listener
    };
  }, [socket, finalSessionId]);

  // Log when animation state changes
  useEffect(() => {
    console.log('Animation state changed to:', animationState);
  }, [animationState]);

  // Log when currentPoll changes (for debugging)
  useEffect(() => {
    console.log('Current poll changed:', currentPoll?.id, currentPoll?.displayMode);
  }, [currentPoll]);

  // Handle pending display mode updates when poll becomes available
  useEffect(() => {
    if (pendingDisplayModeUpdate && currentPoll && String(currentPoll.id) === String(pendingDisplayModeUpdate.pollId)) {
      console.log('=== HANDLING PENDING DISPLAY MODE UPDATE ===');
      console.log('Pending update:', pendingDisplayModeUpdate);
      console.log('Current poll:', currentPoll);
      
      const data = pendingDisplayModeUpdate;
      
      // Function to handle the display mode update
      const handleDisplayModeUpdate = () => {
        console.log('✅ Poll ID matches! Updating poll display mode with animation:', data.displayMode);
        
        // Start fade out animation
        setAnimationState('fadeOut');
        
        // Clear any existing timeouts
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        // After fade out completes, update the poll and fade in
        timeoutRef.current = setTimeout(() => {
          // Update the current poll's display mode
          setCurrentPoll(prev => ({
            ...prev,
            displayMode: data.displayMode
          }));
          
          // If switching to results mode, fetch the results
          if (data.displayMode === 'results') {
            console.log('Fetching poll results for display');
            api.get(`/api/polls/${data.pollId}/with-results`)
              .then(response => {
                const pollWithResults = response.data;
                console.log('Fetched poll with results:', pollWithResults);
                
                setCurrentPoll(prev => ({
                  ...prev,
                  results: pollWithResults.results
                }));
                
                // Start fade in animation
                setAnimationState('fadeIn');
                
                // Reset animation state after fade in completes
                timeoutRef.current = setTimeout(() => {
                  setAnimationState('none');
                }, 500);
              })
              .catch(err => {
                console.error('Error fetching poll results:', err);
                
                // Still fade in even if results fetch fails
                setAnimationState('fadeIn');
                timeoutRef.current = setTimeout(() => {
                  setAnimationState('none');
                }, 500);
              });
          } else {
            // If switching to question mode, clear results
            setCurrentPoll(prev => ({
              ...prev,
              results: null
            }));
            
            // Start fade in animation
            setAnimationState('fadeIn');
            
            // Reset animation state after fade in completes
            timeoutRef.current = setTimeout(() => {
              setAnimationState('none');
            }, 500);
          }
        }, 500); // Wait for fade out to complete
      };
      
      handleDisplayModeUpdate();
      
      // Clear the pending update
      setPendingDisplayModeUpdate(null);
      
      console.log('=== END HANDLING PENDING DISPLAY MODE UPDATE ===');
    }
  }, [currentPoll, pendingDisplayModeUpdate]);


  console.log('Rendering with animation state:', animationState);
  console.log('Current question:', currentQuestion);
  console.log('Current poll:', currentPoll);
  console.log('Template:', template);
  
  // Create particle effect for reactions
  const createParticle = (reaction, position = null) => {
    const particle = {
      id: Date.now() + Math.random(),
      reaction,
      x: position?.x || Math.random() * window.innerWidth,
      y: position?.y || window.innerHeight - 100,
      scale: 0.5,
      opacity: 1,
      velocity: {
        x: (Math.random() - 0.5) * 2,
        y: -Math.random() * 3 - 2
      }
    };
    
    setParticles(prev => [...prev, particle]);
    
    // Animate particle
    const animate = () => {
      setParticles(prev => prev.map(p => {
        if (p.id === particle.id) {
          const newParticle = {
            ...p,
            x: p.x + p.velocity.x,
            y: p.y + p.velocity.y,
            scale: Math.min(p.scale + 0.02, 1.2),
            opacity: Math.max(p.opacity - 0.01, 0),
            velocity: {
              x: p.velocity.x * 0.98,
              y: p.velocity.y - 0.1
            }
          };
          
          if (newParticle.opacity <= 0 || newParticle.y < -100) {
            return null;
          }
          return newParticle;
        }
        return p;
      }).filter(Boolean));
    };
    
    const interval = setInterval(animate, 16);
    setTimeout(() => clearInterval(interval), 3000);
  };
  
  const renderContent = () => {
    console.log('Rendering content with:', { 
      currentQuestion, 
      currentPoll,
      template, 
      animationState
    });
    
    // If we have a poll to show, render the poll
    if (currentPoll && template) {
      console.log('Rendering poll:', currentPoll);
      console.log('Poll options:', currentPoll.options);
      console.log('Options type:', typeof currentPoll.options);
      console.log('Poll display mode:', currentPoll.displayMode);
      
      try {
        // Parse options if it's a string, or use as-is if it's already an array
        let options = currentPoll.options;
        if (typeof options === 'string') {
          try {
            options = JSON.parse(options);
          } catch (parseErr) {
            console.error('Error parsing poll options JSON:', parseErr);
            options = [];
          }
        }
        
        // Ensure options is an array
        if (!Array.isArray(options)) {
          console.error('Poll options is not an array:', options);
          options = [];
        }
        
        console.log('Processed options:', options);
        
        // Get translations based on session poll settings language
        const language = pollSettings.language || 'en';
        const translations = {
          en: {
            votes: 'votes',
            total: 'Total'
          },
          sv: {
            votes: 'röster',
            total: 'Totalt'
          }
        };
        const t = translations[language] || translations.en;
        
        // Create poll HTML based on display mode
        let pollHtml;
        
        if (currentPoll.displayMode === 'results' && currentPoll.results) {
          // Show results mode
          console.log('Rendering poll with results:', currentPoll.results);
          
          // Calculate total votes
          const totalVotes = currentPoll.results.reduce((sum, result) => sum + result.count, 0);
          
          pollHtml = `
            <div class="poll-container">
              <div class="poll-question">${currentPoll.question || 'No question'}</div>
              <div class="poll-results">
                ${options.map((option, index) => {
                  const result = currentPoll.results.find(r => r.option_index === index);
                  const votes = result ? result.count : 0;
                  const percentage = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
                  
                  return `
                    <div class="poll-result" data-index="${index}">
                      <div class="result-header">
                        <span class="option-letter">${String.fromCharCode(65 + index)}</span>
                        <span class="option-text">${option}</span>
                        <span class="vote-count">${votes} ${t.votes} (${percentage}%)</span>
                      </div>
                      <div class="progress-bar">
                        <div class="progress-fill" style="width: ${percentage}%"></div>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
              <div class="total-votes">${t.total}: ${totalVotes} ${t.votes}</div>
            </div>
          `;
        } else {
          // Show question mode (default)
          pollHtml = `
            <div class="poll-container">
              <div class="poll-question">${currentPoll.question || 'No question'}</div>
              <div class="poll-options">
                ${options.map((option, index) => `
                  <div class="poll-option" data-index="${index}">
                    <span class="option-letter">${String.fromCharCode(65 + index)}</span>
                    <span class="option-text">${option}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          `;
        }
        
        // Get gradient colors from session poll settings
        const gradientStart = pollSettings.gradient_start || '#667eea';
        const gradientEnd = pollSettings.gradient_end || '#764ba2';
        
        // Add poll-specific CSS
        const pollCss = `
          .animated-container {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            width: 100%;
          }
          
          .poll-container {
            background: linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%);
            color: white;
            padding: 40px;
            border-radius: 20px;
            text-align: center;
            max-width: 800px;
            width: 90%;
          }
          
          .poll-question {
            font-size: 36px;
            font-weight: bold;
            margin-bottom: 30px;
            line-height: 1.3;
          }
          
          .poll-options {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
            margin-top: 30px;
          }
          
          .poll-option {
            background: rgba(255, 255, 255, 0.2);
            padding: 20px;
            border-radius: 15px;
            display: flex;
            align-items: center;
            gap: 15px;
            transition: all 0.3s ease;
            cursor: pointer;
            opacity: 0;
            animation: slideInFromLeft 0.6s ease-out forwards;
          }
          
          .poll-option:nth-child(1) { animation-delay: 0.1s; }
          .poll-option:nth-child(2) { animation-delay: 0.2s; }
          .poll-option:nth-child(3) { animation-delay: 0.3s; }
          .poll-option:nth-child(4) { animation-delay: 0.4s; }
          .poll-option:nth-child(5) { animation-delay: 0.5s; }
          
          .poll-option:hover {
            background: rgba(255, 255, 255, 0.3);
            transform: translateY(-2px);
          }
          
          .poll-results {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
            margin-top: 30px;
          }
          
          .poll-result {
            background: rgba(255, 255, 255, 0.2);
            padding: 20px;
            border-radius: 15px;
            text-align: left;
          }
          
          .result-header {
            display: flex;
            align-items: center;
            gap: 15px;
            margin-bottom: 10px;
          }
          
          .option-letter {
            background: rgba(255, 255, 255, 0.3);
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 18px;
            flex-shrink: 0;
          }
          
          .option-text {
            font-size: 20px;
            flex: 1;
          }
          
          .vote-count {
            font-size: 16px;
            font-weight: bold;
            color: rgba(255, 255, 255, 0.9);
          }
          
          .progress-bar {
            width: 100%;
            height: 8px;
            background: rgba(255, 255, 255, 0.2);
            border-radius: 4px;
            overflow: hidden;
          }
          
          .progress-fill {
            height: 100%;
            background: rgba(255, 255, 255, 0.8);
            border-radius: 4px;
            transition: width 0.6s ease-in-out;
            transform-origin: left;
            position: relative;
            overflow: hidden;
          }
          
          .progress-fill::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
            animation: shimmer 2s infinite;
          }
          
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          
          .poll-result {
            opacity: 0;
            animation: slideInFromLeft 0.6s ease-out forwards;
          }
          
          .poll-result:nth-child(1) { animation-delay: 0.1s; }
          .poll-result:nth-child(2) { animation-delay: 0.2s; }
          .poll-result:nth-child(3) { animation-delay: 0.3s; }
          .poll-result:nth-child(4) { animation-delay: 0.4s; }
          .poll-result:nth-child(5) { animation-delay: 0.5s; }
          
          @keyframes slideInFromLeft {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          .total-votes {
            margin-top: 20px;
            font-size: 18px;
            font-weight: bold;
            color: rgba(255, 255, 255, 0.9);
          }
        `;
        
        return (
          <div className={`animated-container ${animationState}`}>
            <style>{pollCss}</style>
            <div dangerouslySetInnerHTML={{ __html: pollHtml }} />
          </div>
        );
      } catch (err) {
        console.error('Error rendering poll:', err);
        return <div className="error">Error rendering poll: {err.message}</div>;
      }
    }
    
    // If we should show QR code
    if (showQRCode && qrCodeDataUrl) {
      console.log('Rendering QR code display with mode:', qrTemplate.display_mode);
      
      const qrSettings = getActiveQrSettings(qrTemplate);

      // Lower third template
      if (qrTemplate.display_mode === 'lower-third') {
        const hasText = hasQrCallToAction(qrSettings.call_to_action);
        const lowerThirdBackground = qrBackgroundColor(qrSettings);
        const qrDisplaySize = qrSettings.qr_size;
        return (
          <div className={`animated-container ${qrAnimationState}`} style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundColor: 'transparent'
          }}>
            <style>{`
              /* Default QR Lower Third Styles - Customize in Template Editor */
              .qr-lower-third-container {
                position: absolute;
                left: ${qrSettings.position_x}%;
                top: ${qrSettings.position_y}%;
                transform: translate(-50%, -50%);
                background: ${lowerThirdBackground};
                border-radius: ${qrSettings.border_radius}px;
                padding: ${qrSettings.padding}px;
                box-shadow: ${qrSettings.shadow_offset_x}px ${qrSettings.shadow_offset_y}px ${qrSettings.shadow_blur}px rgba(0, 0, 0, ${qrSettings.shadow_opacity});
                display: flex;
                align-items: center;
                gap: ${Math.max(8, Math.round(qrSettings.padding))}px;
                max-width: 90%;
                min-width: ${hasText ? '200px' : '0'};
                width: auto;
              }
              
              .qr-lower-third-container.qr-no-text {
                min-width: 0;
                padding: ${qrSettings.padding}px;
              }
              
              .qr-lower-third-text {
                flex: 1;
                text-align: left;
              }
              
              .qr-lower-third-call-to-action {
                font-size: 24px;
                font-weight: bold;
                line-height: 1.3;
                margin: 0;
                color: ${qrSettings.text_color};
              }
              
              .qr-lower-third-code-section {
                background: white;
                padding: 12px;
                border-radius: ${Math.max(0, Math.round(qrSettings.border_radius * 0.6))}px;
                box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
                flex-shrink: 0;
              }
              
              .qr-lower-third-code-image {
                display: block;
                width: ${qrDisplaySize}px;
                height: ${qrDisplaySize}px;
              }
              
              /* Custom CSS from Template Editor */
              ${qrSettings.custom_css || ''}
            `}</style>
            <div className={`qr-lower-third-container ${hasText ? '' : 'qr-no-text'}`}>
              {hasText && (
                <div className="qr-lower-third-text">
                  <h1 className="qr-lower-third-call-to-action">{qrSettings.call_to_action}</h1>
                </div>
              )}
              <div className="qr-lower-third-code-section">
                <img 
                  src={qrCodeDataUrl} 
                  alt="Scan to ask a question" 
                  className="qr-lower-third-code-image"
                />
              </div>
            </div>
          </div>
        );
      }
      
      // Fullscreen template (default)
      const hasFullscreenText = hasQrCallToAction(qrSettings.call_to_action);
      const fullscreenBackground = qrSettings.background_image
        ? 'transparent'
        : qrBackgroundColor(qrSettings);
      const fullscreenQrSize = qrSettings.qr_size;
      return (
        <div className={`animated-container ${qrAnimationState}`} style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          backgroundColor: fullscreenBackground,
          backgroundImage: qrSettings.background_image ? `url(${qrSettings.background_image})` : 'none',
          backgroundSize: qrSettings.background_image ? 'cover' : 'auto',
          backgroundPosition: qrSettings.background_image ? 'center' : 'auto',
          backgroundRepeat: qrSettings.background_image ? 'no-repeat' : 'repeat'
        }}>
          <style>{`
            /* Default QR Fullscreen Styles - Customize in Template Editor */
            .qr-display-container {
              position: absolute;
              left: ${qrSettings.position_x}%;
              top: ${qrSettings.position_y}%;
              transform: translate(-50%, -50%);
              display: flex;
              flex-direction: row;
              align-items: center;
              gap: ${Math.max(8, qrSettings.padding)}px;
              padding: ${qrSettings.padding}px;
              max-width: 1400px;
            }
            
            .qr-text-section {
              flex: 1;
              text-align: left;
            }
            
            .qr-call-to-action {
              font-size: 72px;
              font-weight: bold;
              line-height: 1.2;
              margin: 0;
              color: ${qrSettings.text_color};
            }
            
            .qr-code-section {
              background: white;
              padding: 30px;
              border-radius: ${qrSettings.border_radius}px;
              box-shadow: ${qrSettings.shadow_offset_x}px ${qrSettings.shadow_offset_y}px ${qrSettings.shadow_blur}px rgba(0, 0, 0, ${qrSettings.shadow_opacity});
            }
            
            .qr-code-image {
              display: block;
              width: ${fullscreenQrSize}px;
              height: ${fullscreenQrSize}px;
            }
            
            /* Custom CSS from Template Editor */
            ${qrSettings.custom_css || ''}
          `}</style>
          <div className="qr-display-container">
            {hasFullscreenText && (
              <div className="qr-text-section">
                <h1 className="qr-call-to-action">{qrSettings.call_to_action}</h1>
              </div>
            )}
            <div className="qr-code-section">
              <img 
                src={qrCodeDataUrl} 
                alt="Scan to ask a question" 
                className="qr-code-image"
              />
            </div>
          </div>
        </div>
      );
    }
    
    // If we have a question to show, render the question
    if (currentQuestion && template) {
      console.log('Rendering question:', currentQuestion);

      try {
      // Process the HTML template manually instead of using Handlebars
      let html = template.html;
      
      // Replace content placeholder
      html = html.replace(/\{\{content\}\}/g, currentQuestion.content || currentQuestion.text || '');
      
      // Handle submitter name with conditional rendering
      if (currentQuestion.submitterName) {
        console.log('Rendering with submitter name:', currentQuestion.submitterName);
        // Replace submitterName placeholder
        html = html.replace(/\{\{submitterName\}\}/g, currentQuestion.submitterName);
        // Also replace {{submitter}} for backward compatibility
        html = html.replace(/\{\{submitter\}\}/g, currentQuestion.submitterName);
        // Handle conditional blocks for submitterName and submitter
        html = html.replace(/\{\{#if submitterName\}\}([\s\S]*?)\{\{\/if\}\}/g, '$1');
        html = html.replace(/\{\{#if submitter\}\}([\s\S]*?)\{\{\/if\}\}/g, '$1');
      } else {
        console.log('No submitter name found in question');
        // Remove conditional blocks for submitterName and submitter
        html = html.replace(/\{\{#if submitterName\}\}[\s\S]*?\{\{\/if\}\}/g, '');
        html = html.replace(/\{\{#if submitter\}\}[\s\S]*?\{\{\/if\}\}/g, '');
      }

      console.log('Final HTML after processing:', html);
      
      // Special handling for Full Screen template
      if (template.name === "Full Screen") {
        return (
          <div className={`animated-container ${animationState}`}>
            <style>{template.css}</style>
            <div dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        );
      }
      
      return (
        <div className={`animated-container ${animationState}`}>
          <style>{template.css}</style>
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      );
      } catch (err) {
        console.error('Error rendering template:', err);
        return <div className="error">Error rendering template: {err.message}</div>;
      }
    }
    
    // If no content to show
    return null;
  };

  return (
    <div className="display-view">
      <MetaTags 
        title={`${sessionName || 'Live Display'} - AskTide`}
        description="Live broadcast display for questions and polls. Professional overlay for OBS, vMix, and Tricaster integration."
        url={window.location.href}
      />
      <style>{`
        .display-view {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: transparent;
          overflow: hidden;
        }
        
        .animated-container {
          opacity: 1;
          z-index: 10;
        }
        
        .animated-container.fadeIn {
          animation: fadeIn 0.5s ease forwards;
        }
        
        .animated-container.fadeOut {
          animation: fadeOut 0.5s ease forwards;
        }
        
        .animated-container.none {
          opacity: 1;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        .name-tag-container {
          transition: opacity 0.5s ease-in-out;
        }
        
        .name-tag-container.fade-in {
          animation: nameTagFadeIn 0.5s ease-in-out forwards;
        }
        
        .name-tag-container.fade-out {
          animation: nameTagFadeOut 0.5s ease-in-out forwards;
        }
        
        @keyframes nameTagFadeIn {
          from { 
            opacity: 0; 
            transform: scale(0.95);
          }
          to { 
            opacity: 1; 
            transform: scale(1);
          }
        }
        
        @keyframes nameTagFadeOut {
          from { 
            opacity: 1; 
            transform: scale(1);
          }
          to { 
            opacity: 0; 
            transform: scale(0.95);
          }
        }
        
        .error {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: red;
          background-color: rgba(255, 255, 255, 0.8);
          padding: 20px;
          border-radius: 8px;
        }
      `}</style>
      
      {error && <div className="error">{error}</div>}
      {renderContent()}
      
      {/* Particle Effects */}
      {particles.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {particles.map(particle => (
            <div
              key={particle.id}
              className="absolute text-4xl select-none"
              style={{
                left: particle.x,
                top: particle.y,
                transform: `scale(${particle.scale})`,
                opacity: particle.opacity,
                transition: 'all 0.1s ease-out'
              }}
            >
              {particle.reaction}
            </div>
          ))}
        </div>
      )}

      {/* Name Tags */}
      {nameTags.map(nameTag => {
        // Show name tag if it's on air OR if it's currently fading out
        const shouldShow = nameTag.is_on_air || nameTagAnimations[nameTag.id] === 'fade-out';
        if (!shouldShow) return null;
        
        // Use individual name tag settings if provided, otherwise use global settings
        const backgroundColor = nameTag.background_color !== null && nameTag.background_color !== undefined
          ? nameTag.background_color
          : nameTagSettings.background_color;
        const nameColor = nameTag.name_color !== null && nameTag.name_color !== undefined
          ? nameTag.name_color
          : nameTagSettings.name_color;
        const subtitleColor = nameTag.subtitle_color !== null && nameTag.subtitle_color !== undefined
          ? nameTag.subtitle_color
          : nameTagSettings.subtitle_color;
        
        return (
        <div
          key={nameTag.id}
          className={`fixed pointer-events-none z-40 name-tag-container ${
            nameTagAnimations[nameTag.id] || ''
          }`}
          style={{
            position: 'fixed',
            top: nameTag.placement?.includes('top') ? '40px' : 'auto',
            bottom: nameTag.placement?.includes('bottom') ? '120px' : 'auto',
            left: nameTag.placement?.includes('left') ? '80px' : 'auto',
            right: nameTag.placement?.includes('right') ? '80px' : 'auto',
            backgroundColor: backgroundColor,
            color: nameColor,
            padding: nameTagSettings.padding || '12px 20px',
            borderRadius: nameTagSettings.borderRadius || '8px',
            fontFamily: 'Source Sans Pro, sans-serif',
            textAlign: nameTag.placement?.includes('right') ? 'right' : 'left',
            minWidth: nameTagSettings.minWidth || '300px',
            maxWidth: nameTagSettings.maxWidth || '300px',
            zIndex: 40
          }}
        >
          <div 
            style={{ 
              fontSize: nameTagSettings.name_font_size || '24px', 
              fontWeight: nameTagSettings.name_font_weight || 'bold',
              fontFamily: 'Source Sans Pro, sans-serif',
              color: nameColor
            }}
          >
            {nameTag.name}
          </div>
          {nameTag.subtitle && (
            <div 
              style={{ 
                fontSize: nameTagSettings.subtitle_font_size || '16px',
                fontWeight: nameTagSettings.subtitle_font_weight || 'normal',
                fontFamily: 'Source Sans Pro, sans-serif',
                color: subtitleColor,
                marginTop: nameTagSettings.subtitle_margin_top || '0px'
              }}
            >
              {nameTag.subtitle}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
};

export default DisplayView; 